import React, { useState } from 'react';
import { CollectionWithProducts } from '@/services/shopify';
import { ProductCard } from './ProductCard';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

interface CollectionSectionProps {
  collection: CollectionWithProducts;
  className?: string;
}
const CollectionSection: React.FC<CollectionSectionProps> = ({
  collection,
  className
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);
  if (!collection.products.edges.length) {
    return null;
  }
  return <section className={className}>
      {/* Collection Header */}
      <div className="mb-6 flex items-start justify-between gap-6">
        <div className="flex-1 flex items-start gap-4">
          <div className="flex-1">
            <h2 className="text-2xl mb-2 md:text-3xl font-normal">{collection.title}</h2>
            {collection.descriptionHtml && (
              <div className="relative">
                <div 
                  className={`text-foreground max-w-2xl prose prose-sm dark:prose-invert ${
                    isMobile && !isExpanded ? 'line-clamp-2' : ''
                  }`}
                  dangerouslySetInnerHTML={{ __html: collection.descriptionHtml }}
                />
                {isMobile && !isExpanded && (
                  <button
                    onClick={() => setIsExpanded(true)}
                    className="text-foreground underline text-sm mt-1 hover:opacity-70 transition-opacity"
                  >
                    Read more
                  </button>
                )}
              </div>
            )}
          </div>
          {collection.image && (
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden bg-secondary/10 flex-shrink-0">
              <img
                src={collection.image.url}
                alt={collection.image.altText || collection.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
        <Button variant="ghost" className="hidden md:flex items-center gap-2 flex-shrink-0" onClick={() => navigate(`/shop/collection/${collection.handle}`)}>
          View All
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Products Horizontal Scroll */}
      <div className="relative">
        <HorizontalScroller showNextCard={true} spacing="custom-2px" snap={false} noLeftPadding={true}>
          {collection.products.edges.map(product => <div key={product.node.id} className="w-[280px] md:w-[320px] flex-shrink-0">
              <ProductCard product={product} />
            </div>)}
        </HorizontalScroller>
      </div>

      {/* Mobile View All Button */}
      <div className="mt-4 md:hidden">
        <Button variant="outline" className="w-full" onClick={() => navigate(`/shop/collection/${collection.handle}`)}>
          View All
        </Button>
      </div>
    </section>;
};
export default CollectionSection;